import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardActions, 
  Button, 
  Chip, 
  Avatar,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  CardGiftcard as GiftIcon, 
  Redeem as RedeemIcon,
  Close as CloseIcon,
  Category as CategoryIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

interface Reward {
  id: string;
  title: string;
  description: string;
  pointsCost: number;
  category: string;
  isActive: boolean;
  maxRedemptions: number;
  currentRedemptions: number;
  createdAt: string;
}

const Rewards: React.FC = () => {
  const { t } = useTranslation();
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReward, setSelectedReward] = useState<Reward | null>(null);
  const [redeemDialogOpen, setRedeemDialogOpen] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    fetchRewards();
  }, []);

  const fetchRewards = async () => {
    try {
      setLoading(true);
      const response = await fetch(getApiUrl(API_ENDPOINTS.REWARDS));
      if (!response.ok) {
        throw new Error('Failed to fetch rewards');
      }
      const data = await response.json();
      setRewards(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rewards');
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemClick = (reward: Reward) => {
    setSelectedReward(reward);
    setRedeemDialogOpen(true);
  };

  const handleRedeemConfirm = async () => {
    if (!selectedReward || !user) return;

    try {
      // TODO: Implement redemption logic
      console.log('Redeeming reward:', selectedReward.id);
      setRedeemDialogOpen(false);
      setSelectedReward(null);
      // Refresh rewards after redemption
      await fetchRewards();
    } catch (err) {
      setError('Failed to redeem reward');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Merchandise':
        return '🛍️';
      case 'Events':
        return '🎫';
      case 'Education':
        return '📚';
      case 'Time Off':
        return '🏖️';
      default:
        return '🎁';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Merchandise':
        return '#2196f3';
      case 'Events':
        return '#ff9800';
      case 'Education':
        return '#4caf50';
      case 'Time Off':
        return '#9c27b0';
      default:
        return '#757575';
    }
  };

  const translateCategory = (category: string): string => {
    const categoryMap: { [key: string]: string } = {
      'Merchandise': t('rewards.categories.merchandise'),
      'Events': t('rewards.categories.events'),
      'Education': t('rewards.categories.education'),
      'Time Off': t('rewards.categories.timeOff')
    };
    return categoryMap[category] || category;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Button onClick={fetchRewards} variant="outlined">
          {t('rewards.retry')}
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <GiftIcon sx={{ fontSize: '2rem' }} />
        {t('rewards.titleFull')}
      </Typography>

      {user && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            {t('rewards.yourPointsBalance')}: {user.pointsBalance || 0} {t('rewards.points')}
          </Typography>
        </Box>
      )}

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
        gap: 3 
      }}>
        {rewards.map((reward) => {
          const canAfford = user && (user.pointsBalance || 0) >= reward.pointsCost;
          const isAvailable = reward.isActive && reward.currentRedemptions < reward.maxRedemptions;
          const canRedeem = canAfford && isAvailable;

          return (
            <Card key={reward.id} sx={{ 
              height: '100%', 
              display: 'flex', 
              flexDirection: 'column',
              border: reward.category === 'Time Off' ? '2px solid #9c27b0' : '1px solid #e0e0e0',
              '&:hover': {
                boxShadow: 3,
                transform: 'translateY(-2px)',
                transition: 'all 0.2s ease-in-out'
              }
            }}>
              <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Avatar
                  sx={{
                    width: 60,
                    height: 60,
                    mx: 'auto',
                    mb: 2,
                    fontSize: '2rem',
                    bgcolor: getCategoryColor(reward.category),
                    color: 'white'
                  }}
                >
                  {getCategoryIcon(reward.category)}
                </Avatar>
                
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {reward.title}
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: '3rem' }}>
                  {reward.description}
                </Typography>
                
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
                    {reward.pointsCost} {t('rewards.points')}
                  </Typography>
                  <Chip
                    label={translateCategory(reward.category)}
                    size="small"
                    icon={<CategoryIcon />}
                    sx={{ bgcolor: getCategoryColor(reward.category), color: 'white' }}
                  />
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                  {!canAfford && (
                    <Chip label={t('rewards.notEnoughPoints')} color="error" size="small" />
                  )}
                  {!isAvailable && (
                    <Chip label={t('rewards.unavailable')} color="default" size="small" />
                  )}
                  {reward.maxRedemptions && (
                    <Chip 
                      label={`${reward.currentRedemptions}/${reward.maxRedemptions} ${t('rewards.used')}`} 
                      color="info" 
                      size="small" 
                    />
                  )}
                </Box>
              </CardContent>
              
              <CardActions sx={{ justifyContent: 'center', pb: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<RedeemIcon />}
                  disabled={!canRedeem}
                  fullWidth
                  sx={{ mx: 1 }}
                  onClick={() => handleRedeemClick(reward)}
                >
                  {canRedeem ? t('rewards.redeem') : t('rewards.cannotRedeem')}
                </Button>
              </CardActions>
            </Card>
          );
        })}
      </Box>

      {/* Redemption Dialog */}
      <Dialog open={redeemDialogOpen} onClose={() => setRedeemDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {t('rewards.confirmRedemption')}
          <IconButton
            aria-label="close"
            onClick={() => setRedeemDialogOpen(false)}
            sx={{ color: (theme) => theme.palette.grey[500] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {selectedReward && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                {selectedReward.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selectedReward.description}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{t('rewards.pointsCost')}:</strong> {selectedReward.pointsCost} {t('rewards.points')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                <strong>{t('rewards.yourBalance')}:</strong> {user?.pointsBalance || 0} {t('rewards.points')}
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>{t('rewards.remainingBalance')}:</strong> {(user?.pointsBalance || 0) - selectedReward.pointsCost} {t('rewards.points')}
              </Typography>
              <Alert severity="info">
                {t('rewards.confirmRedemptionMessage')}
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setRedeemDialogOpen(false)}>
            {t('rewards.cancel')}
          </Button>
          <Button 
            onClick={handleRedeemConfirm} 
            variant="contained" 
            color="primary"
            disabled={!selectedReward}
          >
            {t('rewards.confirmRedemptionButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Rewards; 